import {
  COMPLAINTS_ENABLED,
  DEFAULT_LICENSE_STATUS_MAPPING,
} from '@/constants';
import { License } from '@/license/license-types';
import { Set } from 'immutable';
import find from 'lodash/find';
import get from 'lodash/get';
import { createSelector } from 'reselect';
import { AppState } from '../app/app-reducer';
import {
  AdminUpdateConfigType,
  PropertyOverviewTabs,
  TabConfig,
} from '../types';

export const propertyList = (state) => state.data.propertyList;
export const str = (state) => state.data.str?.data;
export const ltr = (state) => state.data.ltr?.data;
export const kpiData = (state) => state.data.kpiData;

export const stats = (state) => state.data.stats;

export const translations = (state) => state.data.translations;

export const getPropertyData = (state, deckardid) => {
  const pList = propertyList(state);
  return find(pList, (dat) => dat.deckardid === deckardid);
};

export const hoa = (state) => state.data.hoa;

export const hoaDropdownOpts = createSelector(hoa, (hoaList) =>
  hoaList?.map((hoaName) => ({ value: hoaName, label: hoaName }))
);

export const rentalscapeStats = createSelector(
  stats,
  (statsObj) => statsObj?.rentalscape
);

export const isExpressSelector = createSelector(
  stats,
  (statsObj) => statsObj?.licensing?.config_version === 'express-str'
);

export const licenseEditModeSelector = createSelector(
  stats,
  (statsObj): AdminUpdateConfigType | undefined =>
    statsObj?.rentalscape?.licenseEditMode
);

export const cityPlaceNS = createSelector(stats, (statsObj) => {
  const { placens, countyns } = statsObj?.rentalscape || {};
  return { placens, countyns };
});

export const customerConfigIncludesCities = createSelector(stats, (statsObj) =>
  Boolean(statsObj?.rentalscape?.includeCities)
);

export const cityDisplayName = createSelector(
  stats,
  (statsObj) => statsObj?.rentalscape?.name
);

export const licenseStatusMapping = createSelector(
  stats,
  (statsObj) =>
    statsObj.rentalscape?.licensing?.statusMapping ||
    DEFAULT_LICENSE_STATUS_MAPPING
);

type PropertyDetailsTabs = {
  [key in PropertyOverviewTabs]?: {
    enabled?: boolean;
    [key: string]: unknown;
  };
};

export const propertyDetailsTabs = createSelector(
  stats,
  (statsObj): PropertyDetailsTabs => statsObj.rentalscape?.propertyDetails?.tabs
);

export const getFromPropertyDetailsTabs = (
  path: string
): TabConfig | undefined =>
  createSelector(propertyDetailsTabs, (tabs) => get(tabs, path));

export const licenseEditButtonConfig = createSelector(
  stats,
  (statsObj) => statsObj.rentalscape?.licenseEditButton
);

export const registrationPortalPlaceShortName = createSelector(
  stats,
  (statsObj) => statsObj.rentalscape?.registrationPortalPlaceShortName
);

export const betaFeaturesEnabledLegacyFlag = createSelector(
  stats,
  (statsObj) => statsObj.rentalscape?.betaFeatures
);

export const complaintsConfig = createSelector(
  stats,
  (statsObj) => statsObj.rentalscape?.complaints
);

const FORCED_BETA_FEATURE_FLAGS = {
  // [BETA_FEATURES.disableMapClustering]: false,
};

export const adminApplyFlows = createSelector(stats, (statsObj) => {
  return statsObj?.rentalscape?.adminApplyFlows;
});

export const enabledBetaFeatureFlagMap = createSelector(stats, (statsObj) => ({
  ...(statsObj?.rentalscape?.enabledBetaFeatures || {}),
  ...FORCED_BETA_FEATURE_FLAGS,
}));

export const betaFeatureConfigValue = (key) =>
  createSelector(
    stats,
    (statsObj) => statsObj.rentalscape?.enabledBetaFeatures?.[key]
  );

export const enabledBetaFeatures = createSelector(
  enabledBetaFeatureFlagMap,
  (featureToStatus) =>
    Object.keys(featureToStatus).filter((k) => featureToStatus[k])
);

export const isBetaFeatureEnabled = (feature) =>
  createSelector(enabledBetaFeatures, (enabledFeatures) =>
    enabledFeatures.includes(feature)
  );

export const platformIcons = createSelector(stats, (statsObj) => {
  const platformIconsConfig = statsObj?.rentalscape?.platformIcons || {};
  const urlBase = statsObj?.rentalscape?.platformIconUrlBase || '';
  // Add url base to the start of all urls
  return Object.entries(platformIconsConfig).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: { ...value, url: urlBase + value.url },
    }),
    {}
  );
});
export const propertyData = (state) => state.data.propertyData;

export const activityData = (state) => state.data.activityData;

/**
 * This function tells us whether an add_note request is in transit
 *
 * @param {*} state
 * @returns boolean
 */
export const noteAddStatus = (state) => state.data.noteAddStatus;

export type LicensesDataType = string | License[];

export const licensesData = (state: AppState): LicensesDataType => {
  // In case a state like IN_PROCESS or ERROR arrives.
  if (typeof state.data.licensesData === 'string') {
    return state.data.licensesData;
  }
  // `toArray` converts from Immutable to js
  return state.data.licensesData?.toArray();
};

// Returns a list of currently loaded licenses; if there's no license data (loading state, load error or they simply
// were not requested yet), return an empty list.
export const licensesArray = (state: AppState): License[] => {
  // Handle uninitialized state.
  if (state.data.licensesData === null) {
    return [];
  }

  // Possible IN_PROGRESS or ERROR state.
  if (typeof state.data.licensesData === 'string') {
    return [];
  }

  return state.data.licensesData.toArray();
};

export const totPaymentsData = (state: AppState) => {
  // In case a state like IN_PROCESS or ERROR arrives.
  if (typeof state.data.totPaymentsData === 'string') {
    return state.data.totPaymentsData;
  }
  // `toArray` converts from Immutable to js
  return state.data.totPaymentsData?.toArray();
};

export const rentalTypes = createSelector(
  stats,
  (statsData) => statsData.rentalscape.rentalTypes
);

export const licenseTypes = (state) => state.data.stats.licensing.licenseTypes;

export const customLicenseStatusGroups = (state) =>
  state.data.stats.licensing.customLicenseStatusGroups;

export const isHdl = (state) => state.data.stats.rentalscape.hdl;

export const strComplaintStats = createSelector(
  stats,
  (statsData) => statsData?.strComplaint
);

export const strComplaintsMode = createSelector(
  stats,
  (statsData) => statsData?.strComplaint?.mode
);

// This value determines if complaints should be visible (e.g. paid for and available)
export const strComplaintsAvailable = createSelector(
  strComplaintStats,
  (complaintStats) => complaintStats?.mode === COMPLAINTS_ENABLED
);

export const complaintsData = (state) => state.data.complaintsData || [];

export const complaintsByProperty = (state) =>
  state.data.complaintsByProperty || {};

export const propertyComplaintsData = (state) =>
  state.data.propertyComplaintsData || [];

// This value determines if complaints should be accessible
export const complaintsDisabled = createSelector(
  complaintsData,
  (complaints) => !complaints.length
);

export const observedViolationTypes = (state) =>
  state.data.observedViolationTypes || Set();
export const observedPlatforms = (state) =>
  state.data.observedPlatforms || Set();

export const showSuggestionsFlag = createSelector(
  stats,
  (statsObj) => statsObj.rentalscape?.showSuggestions
);

export const jurisdictionTimezone = createSelector(
  stats,
  (statsJSON) => statsJSON?.licensing?.timezone
);
